import { createSelector } from 'reselect';

import { operatorsSelector } from '../data/operators/selectors';
import { parkDetails } from '../data/park/details/selectors';
import { parkResults } from '../data/park/results/selectors';
import { parkReviews } from '../data/park/reviews/selectors';
import { extraData, unitTypes } from '../data/unitTypes/selectors';

import { getOperatorById } from '../data/operators/utils';
import { getParkDetailsById } from '../data/park/details/utils';
import { getParkResultsById } from '../data/park/results/utils';
import { getParkReviewsById } from '../data/park/reviews/utils';
import { getAllUnitTypeDataById } from '../data/unitTypes/utils';

import { dateToDDMMYYYY, DDMMYYYYToDate } from '../../utils/date';
import { searchResultsParamsSelector } from '../search/results/selectors/selectors';
import { getRegionById } from '../data/regions/utils';
import { regionsSelector } from '../data/regions/selectors';

function getResultsData(result, date, unitTypes, extraData) {
  const nights = Object.keys(result)[0];
  const units = result[nights].map((unit) => ({
    ...unit,
    ...(getAllUnitTypeDataById(unitTypes, extraData, unit.unitType) || {}),
    arrivalDate: date,
    nights,
  }));

  return {
    date,
    nights,
    lowestPrice: units.map((unit) => +unit.unitPrice).sort((a, b) => a - b)[0],
    units,
  };
}

export const channelIdSelector = (state) => state.park.channelId;
export const parkIdSelector = (state) => state.park.parkId;
export const selectedDateSelector = (state) => state.park.selectedDate;

export const detailsSelector = (state) => getParkDetailsById(parkDetails(state), parkIdSelector(state));

export const parksRegionNameSelector = createSelector(
  [detailsSelector, regionsSelector],
  (parkDetails, regions) => {
    const regionId = parkDetails?.regn;
    const region = getRegionById(regions, regionId);
    return region?.name || null;
  }
);

export const resultsSelector = (state) => getParkResultsById(parkResults(state), parkIdSelector(state));
export const hasResultsSelector = (state) => {
  const results = resultsSelector(state)?.results;
  return results && Object.keys(results).length > 0;
};
export const reviewsSelector = (state) => getParkReviewsById(parkReviews(state), parkIdSelector(state));

export const operatorSelector = createSelector(detailsSelector, operatorsSelector, (_details, _operators) => {
  return getOperatorById(_operators, _details?.opId);
});

const formatDate = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-GB');
};

const sortByDate = (arr) => {
  return arr.sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });
};

export const processedResultsSelector = createSelector(
  resultsSelector,
  selectedDateSelector,
  unitTypes,
  extraData,
  searchResultsParamsSelector,
  (parkResults, selectedDateStr, unitTypes, extraData, searchResultParams) => {
    const dateSearchedFor = formatDate(searchResultParams?.arrivalDate);
    const selectedDateDate = new Date(selectedDateStr);
    const results = parkResults?.results;
    if (!results) return undefined;

    const all = {};
    const alternativeDatesObj = {};

    Object.keys(results).forEach((dateKey) => {
      all[dateKey] = getResultsData(results[dateKey], DDMMYYYYToDate(dateKey), unitTypes, extraData);

      if (dateKey !== dateSearchedFor) {
        alternativeDatesObj[dateKey] = all[dateKey];
      }
    });

    const selectedDate = !isNaN(selectedDateDate.getTime()) ? all[dateToDDMMYYYY(selectedDateDate)] : undefined;
    const alternativeDates = Object.keys(alternativeDatesObj)?.length
      ? sortByDate(Object.values(alternativeDatesObj))
      : undefined;
    const showAlternativeDates =
      Object.keys(all).length >= 2 ||
      (alternativeDates?.length === 1 && alternativeDates[0]?.date !== selectedDate?.date);

    return {
      all: Object.keys(all).length ? all : undefined,
      originalDateChosen: all[dateSearchedFor],
      alternativeDates,
      selectedDate,
      showAlternativeDates,
    };
  },
);
